import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ForkKnife } from '@phosphor-icons/react';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Button } from '@/components/ui-kit/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { APP_PATHS } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { weekdayNameOf } from '@/lib/utils/custom-date/custom-date';
import {
  useCancelLunchRequest,
  useLunchStatus,
  useMyWorkDays,
  useRequestLunch,
} from '@/modules/lunch/hr-request-lunch/hooks/use-lunch';
import {
  getTargetLunchDay,
  LUNCH_CUTOFF_SECONDS,
} from '@/modules/lunch/hr-request-lunch/types/lunch.types';
import { HomeCard } from '../home-card/home-card';

/**
 * Lunch (Component Map › Home lunch widget): the Order Meal rules (the 11:00 cutoff moves the order to
 * the next day, no lunch off work days, one order a day). One flat divided list of the day's menus with
 * a radio each, then Order lunch; once ordered, the order with Cancel order.
 */
/** The order cutoff as a time of day ("11:00 AM"), on the organization's clock like the rule itself. */
const cutoffLabel = (language: string) =>
  new Date(Date.UTC(2000, 0, 1, 0, 0, LUNCH_CUTOFF_SECONDS)).toLocaleTimeString(language, {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
  });

export const LunchCard = () => {
  const { t, i18n } = useTranslation();
  const date = useMemo(() => getTargetLunchDay(), []);
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);

  const isLunchEnabled = useHasPermission(HRM_PERMISSIONS.PEOPLE.LUNCH_ENABLED);
  const canRequest = useHasPermission(HRM_PERMISSIONS.MEAL.LUNCH_REQUEST);
  const canCancel = useHasPermission(HRM_PERMISSIONS.MEAL.LUNCH_CANCEL);
  const { data: workDays, isLoading: isWorkDaysLoading } = useMyWorkDays(date);
  const isWorkDay = Boolean(workDays?.includes(weekdayNameOf(date) ?? ''));
  const { data: status, isLoading: isStatusLoading } = useLunchStatus(
    date,
    isLunchEnabled && isWorkDay
  );
  const { mutateAsync: requestLunch, isPending: isRequesting } = useRequestLunch();
  const { mutateAsync: cancelLunchRequest, isPending: isCancelling } = useCancelLunchRequest();

  const menus = status?.MenuList ?? [];
  const orderId = status?.ShowLunchCancelButton ? status.FoodRequestId : '';
  const isClosed = status && !status.ShowLunchSubmitButton && !status.ShowLunchCancelButton;

  const order = async () => {
    if (!selectedMenuId) return;
    try {
      const result = await requestLunch(selectedMenuId);
      if (result.IsSuccessful) setSelectedMenuId('');
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  const message = (() => {
    if (!isLunchEnabled) return t('NOT_PERMITTED_FOR_LUNCH');
    if (!isWorkDay) return t('NOT_A_WORK_DAY_FOR_LUNCH');
    if (isClosed) return t(status.MessageKey, { ...status.MessageParams });
    return null;
  })();

  // The day the order is for, e.g. "Sun, Nov 1" (a plain day, never shifted).
  const dayLabel = new Date(`${date}T00:00:00Z`).toLocaleDateString(i18n.language, {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const cutoff = cutoffLabel(i18n.language);

  // Prototype footer: a divider, the deadline line, then the action at the bottom right.
  const footerOf = (note: string, action: ReactNode) => (
    <div className="flex w-full flex-col gap-3 border-t pt-3">
      <p className="text-[13px] text-muted-foreground">{note}</p>
      {action && <div className="flex justify-end">{action}</div>}
    </div>
  );

  const footer = message
    ? undefined
    : orderId
      ? footerOf(
          t('HOME_CANCEL_UNTIL', { time: cutoff, date: dayLabel }),
          canCancel && (
            <Button size="sm" variant="outline" onClick={() => setIsConfirmingCancel(true)}>
              {t('HOME_CANCEL_ORDER')}
            </Button>
          )
        )
      : canRequest && menus.length > 0
        ? footerOf(
            t('HOME_ORDER_UNTIL', { time: cutoff, date: dayLabel }),
            <Button
              size="sm"
              onClick={order}
              loading={isRequesting}
              disabled={!selectedMenuId || isRequesting}
            >
              {t('HOME_ORDER_LUNCH')}
            </Button>
          )
        : undefined;

  return (
    <HomeCard
      title={`${t('LUNCH')} · ${dayLabel}`}
      icon={<ForkKnife />}
      viewTo={APP_PATHS.ORDER_MEAL}
      footer={footer}
    >
      {isWorkDaysLoading || (isLunchEnabled && isWorkDay && isStatusLoading) ? (
        <Skeleton className="h-24 w-full" />
      ) : message ? (
        <p className="text-sm text-muted-foreground">{message}</p>
      ) : orderId ? (
        <div className="flex items-start gap-3 py-1">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-success-subtle text-success">
            <CheckCircle className="size-4" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <b className="truncate text-sm font-semibold">
              {status?.MenuName}
            </b>
            <span className="text-[13px] text-muted-foreground">
              {(status?.RequestedMenu ?? []).join(', ')}
            </span>
          </div>
        </div>
      ) : (
        <RadioGroup
          value={selectedMenuId}
          onValueChange={setSelectedMenuId}
          disabled={!canRequest}
          className="flex flex-col gap-0"
        >
          {menus.map((menu) => (
            <label
              key={menu.ItemId}
              className="flex cursor-pointer items-start gap-3 border-b py-2 last:border-b-0"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">{menu.Name}</span>
                <span className="line-clamp-2 text-xs text-muted-foreground">
                  {menu.MenuItems.join(', ')}
                </span>
              </span>
              <RadioGroupItem value={menu.ItemId} aria-label={menu.Name} className="mt-0.5" />
            </label>
          ))}
        </RadioGroup>
      )}

      <ConfirmationModal
        open={isConfirmingCancel}
        onOpenChange={setIsConfirmingCancel}
        title={t('HOME_CANCEL_ORDER')}
        description={t('HOME_CANCEL_ORDER_CONFIRM')}
        confirmLoading={isCancelling}
        preventAutoClose
        onConfirm={async () => {
          try {
            await cancelLunchRequest(orderId);
          } finally {
            setIsConfirmingCancel(false);
          }
        }}
      />
    </HomeCard>
  );
};
